/**
 * IDT KPI feature API クライアント。`/idt-kpi` の取得 + karte 戦闘力編集を担当する。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business IDT KPI APIクライアント。designer/team/cases 一括取得と karte 戦闘力編集
 */

import { request } from './_request.js';

export type IdtCategory = 'デザイン' | '情報設計' | 'デザインチェック' | 'マスターデザインマージ';
export type IdtCaseStatus = '未対応' | '処理中' | '提出済み' | '完了';
export type IdtCombatPowerSource = 'snapshot' | 'fallback_sheet' | 'none';

/** ステータスラベル → Backlog statusId (編集 PATCH の値)。case-status-map (backend) と一致させる。 @graph-connects none */
export const IDT_STATUS_LABEL_TO_ID: Record<IdtCaseStatus, string> = {
  未対応: '1',
  処理中: '2',
  提出済み: '30503',
  完了: '4',
};

/** Backlog statusId → ステータスラベル (楽観更新で表示を即時反映する用)。 @graph-connects none */
export const IDT_STATUS_ID_TO_LABEL: Record<string, IdtCaseStatus> = {
  '1': '未対応',
  '2': '処理中',
  '30503': '提出済み',
  '4': '完了',
};

export interface IdtDesignerKpi {
  nickname: string;
  combatPower: number | null;
  combatPowerSource: IdtCombatPowerSource;
  totalBusinessDays: number;
  asOfBusinessDays: number;
  capacity: number | null;
  asOfTarget: number | null;
  actualCompleted: number;
  actualInProgress: number;
}

export interface IdtTeamTrendPoint {
  date: string;
  isBusinessDay: boolean;
  plannedCumulative: number;
  /** 当日までの実績累積 pt (完了+処理中)。当日より後の日は null */
  actualCumulative: number | null;
  /** 完了のみの累積 pt (過去 Q view 用)。月 trendDaily では null */
  actualCompletedCumulative: number | null;
}

export interface IdtTeamMonthlySummary {
  month: string;
  capacity: number | null;
  totalActualCompleted: number;
  totalActualInProgress: number;
}

export interface IdtTeamKpi {
  totalCapacity: number;
  asOfTarget: number;
  totalActualCompleted: number;
  totalActualInProgress: number;
  trendDaily: IdtTeamTrendPoint[];
  /** 当該月が属する Q の 3 ヶ月分 team summary (Q view サマリ用) */
  quarterMonths: IdtTeamMonthlySummary[];
  /** Q (3 ヶ月) 全体の日次折線。planned=階段状 / actual=日次累積 (月 view と同ロジック) */
  quarterTrendDaily: IdtTeamTrendPoint[];
  /** Q 内の designer 別「完了のみ」実績 pt。過去 Q view の bullet / donut 用 */
  quarterCompletedByDesigner: Record<string, number>;
  /** 過去 Q の Q view 総目標 override (静的 Q のみ。null は月別 capacity 合算を総目標とする) */
  quarterTargetOverride: number | null;
}

export interface IdtCaseRow {
  /** 案件番号 (母票 = 親チケット issue_key) */
  issueKey: string;
  /** 子チケット自身の issue_key (実績/ステータス編集の PATCH 対象) */
  childIssueKey: string;
  summary: string;
  designer: string;
  designerTeam: string | null;
  pl: string | null;
  mainWorker: string | null;
  category: IdtCategory;
  actualPt: number;
  plannedPtMin: number | null;
  plannedPtMax: number | null;
  status: IdtCaseStatus;
  /** 子チケット status.id (Backlog 生値) */
  statusId: string | null;
  /** 完了更新日 "YYYY-MM-DD" (完了案件のみ。未完了は null) */
  finishedDate: string | null;
}

export interface IdtKpiResponse {
  month: string;
  asOfDate: string;
  isCurrentMonth: boolean;
  designers: IdtDesignerKpi[];
  team: IdtTeamKpi;
  cases: IdtCaseRow[];
  localDryRunMode: boolean;
}

/**
 * GET /idt-kpi/active-cases レスポンス。担当案件一覧 (未完了・直近1ヶ月更新・期間非依存)。
 * KPI の月次 cases とは別に、選択中の期間ビューに依らず、未完了かつ直近 1 ヶ月以内に
 * 更新のある案件を持つ (更新が 1 ヶ月より前の放置チケットは除外)。
 */
export interface IdtActiveCasesResponse {
  cases: IdtCaseRow[];
}

/** 案件開始時の解像度 5 軸 (企画 3 + 設計 2)。 */
export interface IdtDesignEvaluationResolution {
  /** 【企画】プロジェクトの課題、ゴール設定 */
  issueGoal: number | null;
  /** 【企画】それを解決するタッチポイントの設定 */
  touchpoint: number | null;
  /** 【企画】具体Howの草案（ラフ） */
  howDraft: number | null;
  /** 【設計】Howに基づいたラフ詳細 */
  roughDetail: number | null;
  /** 【設計】Howに基づいた情報設計 */
  infoDesign: number | null;
}

/** チーム評価 3 軸 (貢献度)。 */
export interface IdtDesignEvaluationAxes {
  /** 企画貢献度 */
  planning: number | null;
  /** 設計貢献度 */
  design: number | null;
  /** デザイン貢献度 */
  visual: number | null;
}

/** チーム評価 3 軸の評価対象件数。 */
export interface IdtDesignEvaluationCounts {
  /** 企画_評価対象件数 */
  planning: number | null;
  /** 設計_評価対象件数 */
  design: number | null;
  /** デザイン_評価対象件数 */
  visual: number | null;
}

/** 全体の 1 ヶ月分の評価。 */
export interface IdtDesignEvaluationPoint {
  /** "YYYY-MM" */
  month: string;
  resolution: IdtDesignEvaluationResolution;
  evaluation: IdtDesignEvaluationAxes;
  evaluationCounts: IdtDesignEvaluationCounts;
}

/** 全体の 1 四半期分の評価。 */
export interface IdtDesignEvaluationQuarterPoint {
  /** "YYYY/NQ" (財年制) */
  quarter: string;
  resolution: IdtDesignEvaluationResolution;
  evaluation: IdtDesignEvaluationAxes;
  evaluationCounts: IdtDesignEvaluationCounts;
}

export interface IdtDesignEvaluationResponse {
  /** 全体の月次評価系列 (月昇順) */
  points: IdtDesignEvaluationPoint[];
  /** 全体の四半期評価系列 (Q 昇順) */
  quarterPoints: IdtDesignEvaluationQuarterPoint[];
}

/** デザイナー個人 1 名分の月次 + 四半期評価系列。 */
export interface IdtDesignEvaluationByDesigner {
  /** デザイナー nickname (lower-case) */
  nickname: string;
  /** 月昇順の評価系列 */
  points: IdtDesignEvaluationPoint[];
  /** Q 昇順の四半期評価系列 */
  quarterPoints: IdtDesignEvaluationQuarterPoint[];
}

export interface IdtDesignEvaluationByDesignerResponse {
  /** nickname 別の月次評価系列 */
  designers: IdtDesignEvaluationByDesigner[];
}

/** Good/More 定性コメントの AI 要約 (該当なしは null)。 */
export interface IdtEvaluationCommentSummary {
  good: string | null;
  more: string | null;
}

/** Good/More 定性コメントの明細 (該当なしは空配列)。 */
export interface IdtEvaluationCommentDetail {
  good: string[];
  more: string[];
}

/** 対象月度 × 1 メンバー 1 回答ぶんの評価明細 (「貢献度の定性評価FB」シートの列そのまま)。 */
export interface IdtEvaluationCommentMonthRow {
  /** 回答日 (シート生値) */
  answeredAt: string | null;
  /** メンバー名 (シート生値) */
  member: string;
  /** チケット名 */
  ticket: string | null;
  /** カテゴリ */
  category: string | null;
  /** 重み */
  weight: number | null;
  /** 企画貢献度 */
  planning: number | null;
  /** 設計貢献度 */
  design: number | null;
  /** デザイン貢献度 */
  visual: number | null;
  /** Good コメント */
  good: string | null;
  /** More コメント */
  more: string | null;
}

/** 対象月度 × 全メンバーの評価明細レスポンス。 */
export interface IdtEvaluationCommentMonthDetail {
  /** 絞り込んだ対象月度 (シートが空なら null) */
  month: string | null;
  /** シートに存在する対象月度 (新しい順。月セレクタの候補) */
  months: string[];
  /** 対象月度の全メンバー評価明細 (回答日の降順 = 最新が先頭。回答日なしは末尾) */
  rows: IdtEvaluationCommentMonthRow[];
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business IDT KPI APIクライアント。designer/team/cases 一括取得と karte 戦闘力編集
 * @graph-connects /idt-kpi [calls] IDT KPI 取得 API
 * @graph-connects /idt-kpi/active-cases [calls] 担当案件一覧 (未完了・期間非依存) 取得 API
 * @graph-connects /idt-kpi/design-evaluation [calls] 全体の月次デザイン評価系列取得 API
 * @graph-connects /idt-kpi/design-evaluation/by-designer [calls] デザイナー個人別の月次評価系列取得 API
 * @graph-connects /idt-kpi/design-evaluation/comment-summary [calls] 評価コメント (Good/More) AI 要約取得 API
 * @graph-connects /idt-kpi/design-evaluation/comment-detail [calls] 評価コメント (Good/More) 明細取得 API
 * @graph-connects /idt-kpi/combat-power [calls] designer 戦闘力編集 API
 * @graph-connects /idt-kpi/cases/:childIssueKey [calls] 担当案件明細 実績/ステータス編集 API
 * @graph-connects /idt-kpi/cases/spec-sheet-url [calls] IDpt算出シート URL 解決 API
 */
export const idtKpiApi = {
  getData: (month?: string): Promise<IdtKpiResponse> => {
    const q = month ? `?month=${encodeURIComponent(month)}` : '';
    return request<IdtKpiResponse>(`/idt-kpi${q}`);
  },
  /**
   * 担当案件一覧 (未完了・直近1ヶ月更新・期間非依存) を取得する。KPI の月次 cases とは別に、
   * 選択中の月/四半期に依らず、未完了かつ直近 1 ヶ月以内に更新のある案件を返す (下段テーブル用)。
   */
  getActiveCases: (): Promise<IdtActiveCasesResponse> => {
    return request<IdtActiveCasesResponse>('/idt-kpi/active-cases');
  },
  /** 全体の月次デザイン評価系列 (解像度 5 軸 + 貢献度 3 軸) を全期間取得する。 */
  getDesignEvaluation: (): Promise<IdtDesignEvaluationResponse> => {
    return request<IdtDesignEvaluationResponse>('/idt-kpi/design-evaluation');
  },
  /** デザイナー個人別の月次デザイン評価系列を全期間取得する (karte の個人 vs 全体 前月比較用)。 */
  getDesignEvaluationByDesigner: (): Promise<IdtDesignEvaluationByDesignerResponse> => {
    return request<IdtDesignEvaluationByDesignerResponse>('/idt-kpi/design-evaluation/by-designer');
  },
  /** 対象デザイナー×月の Good/More 定性コメントの AI 要約を取得する (karte の前月コメント要約用)。 */
  getEvaluationCommentSummary: (
    nickname: string,
    month: string,
  ): Promise<IdtEvaluationCommentSummary> => {
    const q = `?nickname=${encodeURIComponent(nickname)}&month=${encodeURIComponent(month)}`;
    return request<IdtEvaluationCommentSummary>(`/idt-kpi/design-evaluation/comment-summary${q}`);
  },
  /** 対象デザイナー×月の Good/More 定性コメントを明細で取得する (MyPage スキル評価のコメント詳細用)。 */
  getEvaluationCommentDetail: (
    nickname: string,
    month: string,
  ): Promise<IdtEvaluationCommentDetail> => {
    const q = `?nickname=${encodeURIComponent(nickname)}&month=${encodeURIComponent(month)}`;
    return request<IdtEvaluationCommentDetail>(`/idt-kpi/design-evaluation/comment-detail${q}`);
  },
  /** 対象月度の全メンバー評価明細を取得する (看板「デザイン評価(全体)」の明細モーダル用)。 */
  getEvaluationCommentMonthDetail: (month?: string): Promise<IdtEvaluationCommentMonthDetail> => {
    const q = month != null && month !== '' ? `?month=${encodeURIComponent(month)}` : '';
    return request<IdtEvaluationCommentMonthDetail>(
      `/idt-kpi/design-evaluation/comment-month-detail${q}`,
    );
  },
  updateCombatPower: (args: {
    name: string;
    month: string;
    value: number;
  }): Promise<{
    success: boolean;
    dryRun: boolean;
    upserted: number;
    missingMembers: string[];
  }> => {
    return request('/idt-kpi/combat-power', {
      method: 'PUT',
      body: JSON.stringify(args),
    });
  },
  /** 子チケットの実績 (actualHours) / ステータス (statusId) を更新する (担当者本人のみ)。 */
  updateCase: (
    childIssueKey: string,
    args: { actualHours?: number; statusId?: string },
  ): Promise<{ success: boolean }> => {
    return request(`/idt-kpi/cases/${encodeURIComponent(childIssueKey)}`, {
      method: 'PUT',
      body: JSON.stringify(args),
    });
  },
  /** 母票 issueKey から IDpt算出シート タブの deep-link を解決する。 */
  getSpecSheetUrl: (issueKey: string): Promise<{ url: string | null }> => {
    return request(`/idt-kpi/cases/spec-sheet-url?issueKey=${encodeURIComponent(issueKey)}`);
  },
};
